import styled from 'styled-components';

export const PageContainer = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  min-height: 70vh;
`;

export const PageTitle = styled.h1`
  font-family: 'Abel', sans-serif;
  font-size: 2.5rem;
  font-weight: 300;
  letter-spacing: 1px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 2rem;
  text-align: center;
`;

export const FormContainer = styled.form`
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

export const Section = styled.div`
  margin-bottom: 2.5rem;
  padding-bottom: 2.5rem;
  border-bottom: 1px solid #e0e0e0;

  &:last-of-type {
    border-bottom: none;
  }
`;

export const SectionTitle = styled.h2`
  font-family: 'Abel', sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const Label = styled.label`
  display: block;
  font-family: 'Abel', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

export const Input = styled.input<{ $error?: boolean }>`
  width: 100%;
  font-family: 'Abel', sans-serif;
  font-size: 1rem;
  padding: 0.75rem 1rem;
  border: 2px solid ${({ $error }) => ($error ? '#ff6b6b' : '#e0e0e0')};
  border-radius: 6px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ $error }) => ($error ? '#ff6b6b' : '#b8964a')};
  }
`;

export const Select = styled.select`
  width: 100%;
  font-family: 'Abel', sans-serif;
  font-size: 1rem;
  padding: 0.75rem 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  background-color: white;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #b8964a;
  }
`;

export const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;

  input[type='checkbox'] {
    width: 20px;
    height: 20px;
    cursor: pointer;
  }

  label {
    font-family: 'Abel', sans-serif;
    font-size: 1rem;
    color: ${({ theme }) => theme.colors.text.primary};
    cursor: pointer;
  }
`;

export const ErrorText = styled.span`
  display: block;
  font-family: 'Abel', sans-serif;
  font-size: 0.85rem;
  color: #ff6b6b;
  margin-top: 0.25rem;
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-top: 2rem;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`;

export const BackButton = styled.button`
  flex: 1;
  background-color: white;
  border: 2px solid #e0e0e0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-family: 'Abel', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  padding: 1rem 2rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    border-color: #b8964a;
    color: #b8964a;
  }
`;

export const SubmitButton = styled.button`
  flex: 2;
  background-color: #b8964a;
  border: none;
  color: white;
  font-family: 'Abel', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  padding: 1rem 2rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    background-color: #a07f3a;
    transform: translateY(-2px);
    box-shadow: 0 4px 14px rgba(184, 150, 74, 0.35);
  }
`;

export const DeliveryOptions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const DeliveryOption = styled.button<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: ${({ selected }) => (selected ? 'rgba(184, 150, 74, 0.08)' : 'white')};
  border: 2px solid ${({ selected }) => (selected ? '#b8964a' : '#e0e0e0')};
  border-radius: 8px;
  cursor: pointer;
  font-family: 'Abel', sans-serif;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: left;
  transition: border-color 0.2s, background-color 0.2s;

  &:hover {
    border-color: #b8964a;
  }
`;

export const DeliveryRadio = styled.span<{ selected: boolean }>`
  display: inline-block;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${({ selected }) => (selected ? '#b8964a' : '#bbb')};
  position: relative;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    inset: 3px;
    border-radius: 50%;
    background-color: ${({ selected }) => (selected ? '#b8964a' : 'transparent')};
  }
`;

export const HintNote = styled.div`
  background-color: rgba(184, 150, 74, 0.08);
  border-left: 4px solid #b8964a;
  padding: 0.85rem 1rem;
  border-radius: 4px;
  font-family: 'Abel', sans-serif;
  font-size: 0.9rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.primary};
`;


export const PickupHintBox = styled.div`
  background-color: rgba(184, 150, 74, 0.06);
  border-left: 4px solid #b8964a;
  padding: 1rem 1.25rem;
  border-radius: 4px;
  font-family: 'Abel', sans-serif;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const PickupHintTitle = styled.div`
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.95rem;
  color: #b8964a;
`;

export const PickupHintText = styled.div`
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 0.75rem;
`;

export const PickupAddressBlock = styled.div`
  font-size: 0.95rem;
  line-height: 1.5;
  padding-top: 0.75rem;
  border-top: 1px dashed rgba(184, 150, 74, 0.4);

  strong {
    display: block;
    margin-bottom: 0.25rem;
    text-transform: uppercase;
    font-size: 0.85rem;
    letter-spacing: 0.5px;
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  font-family: 'Abel', sans-serif;
  font-size: 1rem;
  padding: 0.75rem 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  transition: border-color 0.2s;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: #b8964a;
  }
`;
